












































































































import { Component, Vue } from "vue-property-decorator";
import { firestore, storage } from "../firebaseInit";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { ref, getStorage, uploadBytes } from "firebase/storage";
import {
  Art,
  DEFAULT_PRINT_SIZES,
  SQUARE_PRINT_SIZES,
  displayPathFor,
} from "../models/Art";

@Component({ components: {} })
export default class UploadArt extends Vue {
  private readonly storage = getStorage();
  private readonly FileUploadStatus = Object.freeze({
    INITIAL: "INITIAL",
    SAVING: "SAVING",
    SUCCESS: "SUCCESS",
    FAILED: "FAILED",
  });

  private artArray: Art[] = [];
  private selectedImageUrl: string = "";
  private showSuccessModal: boolean = true;
  private isGalleryUpload: boolean = true;
  private isReproductionsUpload: boolean = false;
  private fileUploadStatus: any = this.FileUploadStatus.INITIAL;
  private fileToUpload: File | null = null;
  private name: string = "";
  private description: string = "";
  private price: number = 100;
  private isActive: boolean = true;
  private reproductionType: 'card' | 'print' = 'card';
  private sizeSet: 'none' | 'standard' | 'square' = 'none';

  get fileSize() {
    if (!this.fileToUpload) return 0;

    const sizeInMB = this.fileToUpload.size / (1024 * 1024);
    if (sizeInMB > 1) return sizeInMB.toFixed(2) + " MB";

    const sizeInKB = this.fileToUpload.size / 1024;

    return sizeInKB.toFixed(0) + " KB";
  }

  get fileSizeInKB() {
    if (!this.fileToUpload) return 0;
    else return (this.fileToUpload.size / 1024).toFixed(0);
  }

  async created() {
    await this.getArtFromDb();
  }

  async getArtFromDb() {
    this.artArray = [];
    const imagesRef = collection(firestore, "home-images");
    const collectionSnap = await getDocs(imagesRef);

    collectionSnap.forEach((doc) => {
      let art = { ...doc.data(), id: doc.id };
      this.artArray.push(art as Art);
    });
  }

  async uploadArt() {
    if (
      !(
        this.name &&
        this.description &&
        this.price !== undefined &&
        this.fileToUpload
      )
    ) {
      alert("Please fill out all fields before submitting.");
      return;
    }

    if (!this.isGalleryUpload && !this.isReproductionsUpload) {
      alert("Please select an Art Type (Gallery or Reproductions) before submitting.");
      return;
    }

    const galleryCollection = collection(firestore, "home-images");
    const reproductionsCollection = collection(firestore, "reproductions");
    const updatePromises: Promise<any>[] = [];
    const galleryDocument: any = {
      name: this.name,
      description: this.description,
      price: this.price,
      createdDate: new Date().toLocaleString(),
      storageLocation: `${this.name}-${new Date().toISOString()}`,
      isActive: this.isActive
    };

    /*
      Every gallery hangs itself from the size of a painting's display copy
      before downloading a single picture (see `displayImageFor`), so a painting
      uploaded without one would be the one piece on the wall that still had to
      be measured the slow way. It is built here, from the same file that was
      just picked, so an upload is complete when it finishes.
    */
    this.fileUploadStatus = this.FileUploadStatus.SAVING;
    const display = await this.buildDisplayCopy(this.fileToUpload);
    if (display) {
      galleryDocument.displayWidth = display.width;
      galleryDocument.displayHeight = display.height;
    }

    const reproductionDocument: any = {...galleryDocument, isReproduction: true, reproductionType: this.reproductionType}

    if (this.reproductionType === 'print' && this.sizeSet !== 'none') {
      const sizes = this.sizeSet === 'square' ? SQUARE_PRINT_SIZES : DEFAULT_PRINT_SIZES;
      reproductionDocument.sizes = sizes.map((size) => ({ ...size }));
    }

    if (this.isGalleryUpload) updatePromises.push(addDoc(galleryCollection, galleryDocument));
    if (this.isReproductionsUpload) updatePromises.push(addDoc(reproductionsCollection, reproductionDocument));
    const storageRef = ref(storage, galleryDocument.storageLocation);
    updatePromises.push(uploadBytes(storageRef, this.fileToUpload));
    if (display) {
      updatePromises.push(
        uploadBytes(
          ref(storage, displayPathFor(galleryDocument.storageLocation)),
          display.blob,
          { contentType: "image/webp" }
        )
      );
    }

    await Promise.all(updatePromises).catch(() => {
      alert("Error uploading art, please try again.");
      return;
    });
    alert("Successfully uploaded art!");
    await this.getArtFromDb();

    this.fileUploadStatus = this.FileUploadStatus.INITIAL;
    this.fileToUpload = null;
    this.name = "";
    this.description = "";
    this.price = 100;
  }

  filesChange(fileList: any[]) {
    if (!fileList.length) return;
    else this.fileToUpload = fileList[0];
  }

  /*
    A copy of the painting at 1600px on its long edge — more than any page on
    the site can show, at a fraction of a master's weight. Matches what
    `scripts/build-display-images.mjs` produces for everything already uploaded.

    Returns null if the browser cannot read the image or cannot write WebP, in
    which case the painting is uploaded without one and the galleries fall back
    to measuring it. A missing display copy costs a slow first load; a broken
    one would cost the upload.
  */
  async buildDisplayCopy(
    file: File
  ): Promise<{ blob: Blob; width: number; height: number } | null> {
    const MAX_EDGE = 1600;
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = reject;
        element.src = objectUrl;
      });

      const scale = Math.min(
        1,
        MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight)
      );
      const width = Math.round(image.naturalWidth * scale);
      const height = Math.round(image.naturalHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.drawImage(image, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/webp", 0.82)
      );

      /* Older Safari ignores the format and hands back a PNG several times the size. */
      if (!blob || blob.type !== "image/webp") return null;
      return { blob, width, height };
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
